/**
 * Live polling of the selected run (epic 031 task 06): while the run is
 * `queued` or `running`, refetch every 2 s; stop on `done` / `failed`; give up
 * after 10 minutes and surface `timedOut` so the screen can show a manual
 * Refresh button (the epic's pre-resolved polling contract). The run list
 * refreshes with the detail — the caller's `refetch` reloads both.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TestRunStatus } from '@leads-platform/shared';

const POLL_INTERVAL_MS = 2_000;
const POLL_MAX_DURATION_MS = 10 * 60_000;

interface UseRunPollingOptions {
  /** The selected run — polling restarts its 10-minute window per run. */
  runId: string | null;
  /** The run's last known status; `null` while no run is loaded yet. */
  status: TestRunStatus | null;
  /** Reload the run detail (and the list). */
  refetch: () => void;
}

interface UseRunPollingResult {
  /** True once 10 minutes elapsed without a terminal status — show Refresh. */
  timedOut: boolean;
  /** Manual refresh — reloads now and restarts the polling window. */
  refresh: () => void;
}

export function useRunPolling({
  runId,
  status,
  refetch,
}: UseRunPollingOptions): UseRunPollingResult {
  const [timedOut, setTimedOut] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const startedAtRef = useRef<number | null>(null);
  const refetchRef = useRef(refetch);

  // Keep the latest callback without re-arming the interval on every render.
  useEffect(() => {
    refetchRef.current = refetch;
  });

  // A different run starts a fresh 10-minute window.
  useEffect(() => {
    startedAtRef.current = null;
    setTimedOut(false);
  }, [runId]);

  useEffect(() => {
    const inFlight = status === 'queued' || status === 'running';
    if (!inFlight || timedOut) {
      startedAtRef.current = null;
      return undefined;
    }
    if (startedAtRef.current === null) {
      startedAtRef.current = Date.now();
    }
    const id = window.setInterval(() => {
      const startedAt = startedAtRef.current;
      if (startedAt !== null && Date.now() - startedAt >= POLL_MAX_DURATION_MS) {
        setTimedOut(true);
        return;
      }
      refetchRef.current();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [status, timedOut, refreshKey]);

  const refresh = useCallback(() => {
    startedAtRef.current = Date.now();
    setTimedOut(false);
    setRefreshKey((key) => key + 1);
    refetchRef.current();
  }, []);

  return { timedOut, refresh };
}
